import { Socket, io } from "socket.io-client"

import { seconds } from "@/3514/utils/time.utils"
import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"

import type {
    ICoachingChatSocketClientEvents as IClientEvents,
    ICoachingChatSocketServerEvents as IServerEvents
} from "../../types"

import { attachCoachingChatSocketDiagnostics } from "./coaching-chat.api.socket.diagnostics"

type TCoachingChatSocket = Socket<IServerEvents, IClientEvents>

const storageService: StorageService = new StorageService(EStorageServiceType.Session)

const authToken: string = storageService.getItem(SessionStorageServiceKeys.ChatSocketToken()) ?? String("")

const coachingChatSocket: TCoachingChatSocket = io(window.location.origin, {
    path: "/ws/socket.io/",
    transports: ["websocket", "polling"],
    autoConnect: false,
    rejectUnauthorized: true,
    secure: true,
    reconnection: true,
    reconnectionAttempts: 3,
    reconnectionDelay: seconds(1),
    reconnectionDelayMax: seconds(10),
    randomizationFactor: 0.5,
    auth: { token: authToken }
})

attachCoachingChatSocketDiagnostics(coachingChatSocket)

const SOCKET_TEARDOWN_GRACE_MS: number = seconds(1.5)

let teardownTimer: ReturnType<typeof setTimeout> | null = null
let socketConsumers: number = 0

function cancelSocketTeardown(): void {
    if (teardownTimer === null) return

    clearTimeout(teardownTimer)
    teardownTimer = null
}

/**
 * Keep the singleton socket alive while at least one eligible chat owns a lease.
 * The consumer count makes replacement mounts safe regardless of whether React
 * runs the old cleanup before or after the new setup.
 */
function acquireCoachingChatSocketLease(): () => void {
    socketConsumers += 1
    cancelSocketTeardown()

    let wasReleased: boolean = false

    return (): void => {
        if (wasReleased) return

        wasReleased = true
        socketConsumers -= 1

        if (socketConsumers > 0) return

        cancelSocketTeardown()
        teardownTimer = setTimeout((): void => {
            teardownTimer = null

            if (socketConsumers === 0) coachingChatSocket.disconnect()
        }, SOCKET_TEARDOWN_GRACE_MS)
    }
}

export { coachingChatSocket, acquireCoachingChatSocketLease }
export type { TCoachingChatSocket }
